import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../../utils/config";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { formatNumero, formatDate } from "../../utils/Utilitats";
import { useUserContext } from "../../utils/UserContext";

/**
 * mode: "nou" | "consultar" | "modificar" | "esborrar"
 * Chrome (capçalera/subnav) el posa Ofertes.js.
 */
function DetallsProducteOferta({
  mode,
  oferta,
  producte,
  productesList,
  onNavigate,
  onSave,
  onCancel,
}) {
  const isReadOnly = mode === "consultar" || mode === "esborrar";
  TeclaEscEnter(onCancel, null);
  const { empresaSeleccionada } = useUserContext();

  const [productCatalog, setProductCatalog] = useState([]);
  const [selectedCatProduct, setSelectedCatProduct] = useState(null);
  const [selectedCategoria, setSelectedCategoria] = useState("");
  const [selectedTipus, setSelectedTipus] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);

  const [quantitat, setQuantitat] = useState(1);
  const [unitatMesura, setUnitatMesura] = useState("ut");
  const [marca, setMarca] = useState("");
  const [model, setModel] = useState("");
  const [refProdProv, setRefProdProv] = useState("");
  const [descripcio, setDescripcio] = useState("");
  const [preuUnitari, setPreuUnitari] = useState("");
  const [error, setError] = useState("");
  const [enviant, setEnviant] = useState(false);

  const categoriesList = selectedCatProduct
    ? [selectedCatProduct.Categoria]
    : selectedMaterial
    ? [...new Set(productCatalog.filter((p) => p.DescripcioMaterial === selectedMaterial).map((p) => p.Categoria))]
    : selectedTipus
    ? [...new Set(productCatalog.filter((p) => p.Tipus === selectedTipus).map((p) => p.Categoria))]
    : [...new Set(productCatalog.map((p) => p.Categoria))];

  const tipusList = selectedCatProduct
    ? [selectedCatProduct.Tipus]
    : selectedMaterial
    ? [...new Set(productCatalog.filter((p) => p.DescripcioMaterial === selectedMaterial).map((p) => p.Tipus))]
    : selectedCategoria
    ? [...new Set(productCatalog.filter((p) => p.Categoria === selectedCategoria).map((p) => p.Tipus))]
    : [...new Set(productCatalog.map((p) => p.Tipus))];

  const materialsList = selectedCatProduct
    ? [selectedCatProduct.DescripcioMaterial]
    : selectedTipus
    ? [...new Set(productCatalog.filter((p) => p.Tipus === selectedTipus).map((p) => p.DescripcioMaterial))]
    : selectedCategoria
    ? [...new Set(productCatalog.filter((p) => p.Categoria === selectedCategoria).map((p) => p.DescripcioMaterial))]
    : [...new Set(productCatalog.map((p) => p.DescripcioMaterial))];

  useEffect(() => {
    let filtered = productCatalog;
    if (selectedCategoria) filtered = filtered.filter((prod) => prod.Categoria === selectedCategoria);
    if (selectedTipus) filtered = filtered.filter((prod) => prod.Tipus === selectedTipus);
    if (selectedMaterial) filtered = filtered.filter((prod) => prod.DescripcioMaterial === selectedMaterial);
    setFilteredProducts(filtered);
  }, [selectedCategoria, selectedTipus, selectedMaterial, productCatalog]);

  const resetForm = useCallback(() => {
    setQuantitat(1);
    setUnitatMesura("ut");
    setMarca("");
    setModel("");
    setRefProdProv("");
    setDescripcio("");
    setPreuUnitari("");
    setSelectedCatProduct(null);
    setSelectedMaterial("");
    setSelectedTipus("");
    setSelectedCategoria("");
    setError("");
  }, []);

  useEffect(() => {
    if (mode === "nou") {
      resetForm();
      return;
    }
    if (!producte) return;
    setQuantitat(producte.Quantitat || 1);
    setUnitatMesura(producte.UnitatMesura || "ut");
    setMarca(producte.Marca || "");
    setModel(producte.Model || "");
    setRefProdProv(producte.RefProdProv || "");
    setDescripcio(producte.Descripcio || "");
    setPreuUnitari(producte.PreuUnitari ?? "");
    setError("");
    if (producte.Productes_idProductes) {
      const catProd = productCatalog.find(
        (p) => Number(p.idProductes) === Number(producte.Productes_idProductes)
      );
      if (catProd) {
        setSelectedCatProduct(catProd);
        setSelectedMaterial(catProd.DescripcioMaterial || "");
        setSelectedTipus(catProd.Tipus || "");
        setSelectedCategoria(catProd.Categoria || "");
      }
    }
  }, [oferta, producte, mode, productCatalog, resetForm]);

  useEffect(() => {
    const fetchProductCatalog = async () => {
      try {
        const resp = await fetch(`${API_URL}/Productes.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada?.IdEmpresa || 0 }),
        });
        const data = await resp.json();
        if (data.success) setProductCatalog(data.productes || []);
      } catch (err) {
        console.error("Error en fetchProductCatalog:", err);
      }
    };
    fetchProductCatalog();
  }, [empresaSeleccionada]);

  const handleSelectCatalog = (id) => {
    if (!id) {
      setSelectedCatProduct(null);
      setSelectedMaterial("");
      setSelectedTipus("");
      setSelectedCategoria("");
      setMarca("");
      setModel("");
      setUnitatMesura("ut");
      setDescripcio("");
      return;
    }
    const catProd = filteredProducts.find((p) => Number(p.idProductes) === Number(id));
    if (catProd) {
      setSelectedCatProduct(catProd);
      setMarca(catProd.Marca || "");
      setModel(catProd.Model || "");
      setUnitatMesura(catProd.UnitatMesura || "ut");
      setDescripcio(catProd.Descripcio || "");
      setSelectedMaterial(catProd.DescripcioMaterial || "");
      setSelectedTipus(catProd.Tipus || "");
      setSelectedCategoria(catProd.Categoria || "");
    }
  };

  const handleSelectMaterial = (material) => {
    setSelectedMaterial(material);
    if (!material) {
      setSelectedTipus("");
      setSelectedCategoria("");
      return;
    }
    const prod = productCatalog.find((p) => p.DescripcioMaterial === material);
    if (prod) {
      setSelectedTipus(prod.Tipus || "");
      setSelectedCategoria(prod.Categoria || "");
    }
  };

  const handleSelectTipus = (tipus) => {
    setSelectedTipus(tipus);
    if (!tipus) {
      setSelectedCategoria("");
      return;
    }
    const prod = productCatalog.find((p) => p.Tipus === tipus);
    if (prod) setSelectedCategoria(prod.Categoria || "");
  };

  const handleSelectCategoria = (categoria) => {
    setSelectedCategoria(categoria);
    setSelectedCatProduct(null);
    setSelectedMaterial("");
    setSelectedTipus("");
  };

  const validarFormulari = () => {
    const errors = [];
    if (!quantitat || Number(quantitat) <= 0) errors.push("La quantitat ha de ser més gran que 0.");
    if (!selectedCatProduct) errors.push("Cal escollir un producte del catàleg.");
    return errors;
  };

  const handleSubmit = async () => {
    if (mode !== "esborrar") {
      const errors = validarFormulari();
      if (errors.length > 0) {
        setError(errors.join(" "));
        return;
      }
    }
    setError("");
    setEnviant(true);

    const producteOfertaData = {
      Ofertes_idOfertes: oferta?.idOfertes || null,
      Quantitat: quantitat,
      RefProdProv: refProdProv,
      PreuUnitari: preuUnitari ? parseFloat(preuUnitari) : 0,
      Productes_idProductes: selectedCatProduct?.idProductes
        ? Number(selectedCatProduct.idProductes)
        : null,
    };

    try {
      if (mode === "nou") {
        const resp = await fetch(`${API_URL}/Nouproducteoferta.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(producteOfertaData),
        });
        const data = await resp.json();
        if (data.success) onSave?.(data.producteOferta);
        else setError(data.message || "Error en crear el producte.");
      } else if (mode === "modificar") {
        const resp = await fetch(`${API_URL}/Modificarproducteoferta.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...producteOfertaData,
            idProductesOfertes: producte?.idProductesOfertes,
          }),
        });
        const data = await resp.json();
        if (data.success) onSave?.({ ...producte, ...producteOfertaData });
        else setError(data.message || "Error en modificar el producte.");
      } else if (mode === "esborrar") {
        const resp = await fetch(`${API_URL}/Esborrarproducteoferta.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idProductesOfertes: producte?.idProductesOfertes }),
        });
        const data = await resp.json();
        if (data.success) onSave?.();
        else setError(data.message || "Error en esborrar el producte.");
      }
    } catch (err) {
      console.error("Error en handleSubmit producte oferta:", err);
      setError("S'ha produït un error de connexió.");
    } finally {
      setEnviant(false);
    }
  };

  const camp = (label, content, mida = "mig", required = false) => {
    const extra =
      mida === "ample"
        ? "dv2-field--ample"
        : mida === "llarg"
        ? "dv2-field--llarg"
        : mida === "curt"
        ? "dv2-field--curt"
        : "dv2-field--mig";
    return (
      <label className={`dv2-field ${extra}`}>
        <span className={`dv2-label ${required && !isReadOnly ? "dv2-required" : ""}`}>{label}</span>
        {content}
      </label>
    );
  };

  const valor = (txt) => <span className="dv2-value">{txt || "—"}</span>;
  const idxNav =
    productesList && producte
      ? productesList.findIndex(
          (p) => Number(p.idProductesOfertes) === Number(producte.idProductesOfertes)
        )
      : -1;
  const potNavegar = mode === "consultar" && productesList && idxNav >= 0 && productesList.length > 1;
  const anarProducte = (delta) => {
    if (!potNavegar) return;
    const n = productesList.length;
    onNavigate?.(productesList[(idxNav + delta + n) % n]);
  };

  return (
    <>
      {mode === "esborrar" && (
        <div className="dv2-banner dv2-banner--danger">
          S'esborrarà aquest producte de l'oferta. El preu total de l'oferta es recalcularà.
        </div>
      )}
      <div className="dv2-entity-body">
        {error ? <p className="dv2-hint">{error}</p> : null}

        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">1</span>
            <h2 className="dv2-card-title">Oferta</h2>
          </div>
          <div className="dv2-fields dv2-fields--sizes">
            {camp("Proveïdor", valor(oferta?.NomProveidor), "llarg")}
            {camp("Data oferta", valor(formatDate(oferta?.DataOferta)))}
            {camp("Referència", valor(oferta?.ReferenciaProv), "llarg")}
          </div>
        </section>

        {!isReadOnly && (
          <section className="dv2-card">
            <div className="dv2-card-head">
              <span className="dv2-card-num">2</span>
              <h2 className="dv2-card-title">Producte de catàleg</h2>
            </div>
            <div className="dv2-fields dv2-fields--sizes">
              {camp(
                "Categoria",
                <select
                  className="dv2-select"
                  value={selectedCategoria}
                  onChange={(e) => handleSelectCategoria(e.target.value)}
                  disabled={enviant}
                >
                  <option value="">Totes</option>
                  {categoriesList.filter(Boolean).map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              )}
              {camp(
                "Tipus",
                <select
                  className="dv2-select"
                  value={selectedTipus}
                  onChange={(e) => handleSelectTipus(e.target.value)}
                  disabled={enviant}
                >
                  <option value="">Tots</option>
                  {tipusList.filter(Boolean).map((tip) => (
                    <option key={tip} value={tip}>
                      {tip}
                    </option>
                  ))}
                </select>
              )}
              {camp(
                "Material",
                <select
                  className="dv2-select"
                  value={selectedMaterial}
                  onChange={(e) => handleSelectMaterial(e.target.value)}
                  disabled={enviant}
                >
                  <option value="">Tots</option>
                  {materialsList.filter(Boolean).map((mat) => (
                    <option key={mat} value={mat}>
                      {mat}
                    </option>
                  ))}
                </select>
              )}
              {camp(
                "Producte",
                <select
                  className="dv2-select"
                  value={selectedCatProduct?.idProductes || ""}
                  onChange={(e) => handleSelectCatalog(e.target.value)}
                  disabled={enviant}
                >
                  <option value="">Selecciona…</option>
                  {filteredProducts.map((prod) => (
                    <option key={prod.idProductes} value={prod.idProductes}>
                      {prod.Marca} {prod.Model}
                    </option>
                  ))}
                </select>,
                "llarg",
                true
              )}
            </div>
          </section>
        )}

        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">{isReadOnly ? "2" : "3"}</span>
            <h2 className="dv2-card-title">Producte, quantitat i preu</h2>
          </div>
          <div className="dv2-fields dv2-fields--sizes">
            {camp("Marca", valor(marca))}
            {camp("Model", valor(model))}
            {camp("Descripció", valor(descripcio), "ample")}
            {camp(
              "Quantitat",
              isReadOnly ? (
                valor(quantitat)
              ) : (
                <input
                  className="dv2-input"
                  type="number"
                  step="1"
                  min="1"
                  value={quantitat}
                  onChange={(e) => setQuantitat(e.target.value)}
                  disabled={enviant}
                />
              ),
              "curt",
              true
            )}
            {camp("Unitat", valor(unitatMesura), "curt")}
            {camp(
              "Preu unitari",
              isReadOnly ? (
                valor(`${formatNumero(preuUnitari)} €`)
              ) : (
                <input
                  className="dv2-input"
                  type="number"
                  step="0.01"
                  value={preuUnitari}
                  onChange={(e) => setPreuUnitari(e.target.value)}
                  disabled={enviant}
                />
              ),
              "curt"
            )}
            {camp(
              "Referència proveïdor",
              isReadOnly ? (
                valor(refProdProv)
              ) : (
                <input
                  className="dv2-input"
                  type="text"
                  value={refProdProv}
                  onChange={(e) => setRefProdProv(e.target.value)}
                  disabled={enviant}
                />
              ),
              "llarg"
            )}
          </div>
        </section>
      </div>

      {(potNavegar || mode === "nou" || mode === "modificar" || mode === "esborrar") && (
        <div className="dv2-entity-dock">
          {potNavegar && (
            <div className="dv2-recordNav">
              <button
                type="button"
                className="dv2-btn dv2-recordNav-btn"
                onClick={() => anarProducte(-1)}
                title="Anterior"
              >
                ‹
              </button>
              <span className="dv2-recordNav-info">
                {idxNav + 1} / {productesList.length}
              </span>
              <button
                type="button"
                className="dv2-btn dv2-recordNav-btn"
                onClick={() => anarProducte(1)}
                title="Següent"
              >
                ›
              </button>
            </div>
          )}
          {(mode === "nou" || mode === "modificar" || mode === "esborrar") && (
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onCancel} disabled={enviant}>
                Cancel·lar
              </button>
              {mode === "esborrar" ? (
                <button
                  type="button"
                  className="dv2-btn dv2-btn--danger"
                  onClick={handleSubmit}
                  disabled={enviant}
                >
                  Esborrar producte
                </button>
              ) : (
                <button
                  type="button"
                  className="dv2-btn dv2-btn--primary"
                  onClick={handleSubmit}
                  disabled={enviant}
                >
                  Desar
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default DetallsProducteOferta;
